import React from 'react'
import { useDrop } from 'react-dnd'

import { setPlanningShiftTableLoading } from '@/entities/gridTables/slices/planningShiftTablesSlice'
import { setPlanningUnassignedTableLoading } from '@/entities/gridTables/slices/planningUnassignedTablesSlice'
import { processDropOnInbox } from '@/entities/gridTables/thunks/processDropOnInbox'
import { updatePlanningShiftTableState } from '@/entities/gridTables/thunks/updatePlanningShiftTableState'
import { updatePlanningUnassignedTableState } from '@/entities/gridTables/thunks/updatePlanningUnassignedTableState'
import { fetchScenarios } from '@/entities/schedules'
import { GridTableDragItem, GridTableDragType } from '@/shared/components/GridTable/models/GridTableDragType'
import { useAppDispatch } from '@/shared/store'
import { pxToRem } from '@/shared/styles/themes'
import { styled } from '@mui/material'

export type PlanningInboxTableDropZoneProps = {
  //
}

export const PlanningInboxTableDropZone: React.FC<PlanningInboxTableDropZoneProps> = () => {
  const dispatch = useAppDispatch()

  // Droppable zone for moving orders to inbox
  const [{ isOver, isDragging }, dropRef] = useDrop({
    accept: [GridTableDragType.SHIFT_STOP_ROW, GridTableDragType.UNASSIGNED_ORDER_ROW],
    drop: async (item: GridTableDragItem) => {
      const orderIds = item.rows.map((stop) => stop.orderId)
      const shiftId = item.rows[0].shiftId
      const scenarioId = item.rows[0].scenarioId

      if (item.type === GridTableDragType.SHIFT_STOP_ROW) {
        dispatch(setPlanningShiftTableLoading({ id: shiftId, isLoading: true }))
        
        await dispatch(processDropOnInbox({ scenarioId, orderIds }))
        await dispatch(fetchScenarios({ scenarioIds: [scenarioId] }))
        await dispatch(updatePlanningShiftTableState({ shiftId: shiftId }))

        dispatch(setPlanningShiftTableLoading({ id: shiftId, isLoading: false }))
      } else if (item.type === GridTableDragType.UNASSIGNED_ORDER_ROW) {
        dispatch(setPlanningUnassignedTableLoading({ id: scenarioId, isLoading: true }))

        await dispatch(processDropOnInbox({ scenarioId, orderIds }))
        await dispatch(fetchScenarios({ scenarioIds: [scenarioId] }))
        await dispatch(updatePlanningUnassignedTableState({ scenarioId: scenarioId }))
        
        dispatch(setPlanningUnassignedTableLoading({ id: scenarioId, isLoading: false }))
      }
    },
    collect: monitor => ({
      isOver: monitor.isOver(),
      isDragging: monitor.getItem() !== null && (
        monitor.getItemType() === GridTableDragType.SHIFT_STOP_ROW ||
        monitor.getItemType() === GridTableDragType.UNASSIGNED_ORDER_ROW
      ),
    }),
  })

  return (
    <StyledInboxDropZone ref={dropRef} $isOver={isOver} $isDragging={isDragging} />
  )
}

const StyledInboxDropZone = styled('div', {
  shouldForwardProp: (prop) => prop !== '$isOver' && prop !== '$isDragging',
}) <{ $isOver: boolean, $isDragging: boolean }>`
  display: ${({ $isDragging }) => ($isDragging ? 'block' : 'none')};
  position: absolute;
  z-index: 1;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${({ theme }) => theme.palette.custom.widgetDropTarget.backgroundColor};
  border-radius: ${pxToRem(8)};
  border: ${({ theme }) => theme.palette.custom.widgetDropTarget.border};
  opacity: ${({ $isOver }) => ($isOver ? 1 : 0)};
  transition: opacity 0.1s;
  padding: ${props => props.theme.typography.pxToRem(8)};
`
